import * as Dialog from "@radix-ui/react-dialog";
import { useForm } from "react-hook-form";
import { useEffect, useState } from "react";
import { zodResolver } from "@hookform/resolvers/zod";
import z from "zod";
import Button from "@/components/ui/button";
import Input from "@/components/ui/input";
import { useWidgets } from "../hooks/use-widgets";
import { getFallbackPreview } from "../constants/widget-templates";
import type { WidgetTemplateListItem } from "../types";
import { usePlan, useBrands } from "@/modules/brand";
import { useOpenUpgradeModal } from "@/hooks/use-upgrade-modal";

interface CreateWidgetModalProps {
	open: boolean;
	onOpenChange: (open: boolean) => void;
	onSuccess: (widgetId: string) => void;
	onSubmit: (data: WidgetFormValues) => Promise<{ success: boolean; data?: { id: string } }>;
}

export type WidgetFormValues = z.infer<typeof widgetFormSchema>;

const widgetFormSchema = z.object({
	title: z.string().min(1, "Widget title is required").max(200, "Max 200 characters"),
	template: z.string().min(1, "Please select a template"),
	template_version: z.number().min(1),
});

function TemplatePreviewImage({ template }: { template: WidgetTemplateListItem }) {
	const fallbackPreview = getFallbackPreview(template.template);

	if (template.preview_image_url) {
		return (
			<div className="h-full w-full bg-gray-50 overflow-hidden">
				<img src={template.preview_image_url} alt={template.label} className="w-full h-full object-contain" />
			</div>
		);
	}

	if (fallbackPreview) {
		return <div className="h-full w-full overflow-hidden">{fallbackPreview}</div>;
	}

	return (
		<div className="h-full w-full bg-gray-50 flex items-center justify-center">
			<span className="text-xs text-gray-400">Preview</span>
		</div>
	);
}

function TemplateItem({
	onClick,
	disabled,
	isSelected,
	template,
	supportedWidgetLevel,
	onUpgradeClick,
}: {
	onClick: () => void;
	disabled: boolean;
	isSelected: boolean;
	template: WidgetTemplateListItem;
	supportedWidgetLevel: number;
	onUpgradeClick: () => void;
}) {
	const isPremium = template.level > supportedWidgetLevel;
	const buttonClasses = isSelected ? "border-brand ring-2 ring-brand/20" : "border-gray-200 hover:border-gray-300";

	if (isPremium) {
		return (
			<button
				type="button"
				onClick={onUpgradeClick}
				className="group flex flex-col h-44 text-left rounded-xl border-2 overflow-hidden transition-all border-gray-200 hover:border-gray-300"
			>
				<div className="relative flex-1 min-h-0">
					<TemplatePreviewImage template={template} />
					<span className="absolute top-2 left-2 px-2 py-0.5 text-[10px] font-medium bg-white/90 text-gray-700 rounded-full shadow-sm capitalize">{template.display_type}</span>

					<span className="absolute top-2 right-2 inline-block p-1 text-blue-600 rounded-lg bg-blue-50 border border-blue-200">
						<svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
							<path strokeLinecap="round" strokeLinejoin="round" d="M13 10V3L4 14h7v7l9-11h-7z" />
						</svg>
					</span>
				</div>
				<div className="px-4 py-3 shrink-0">
					<h4 className="text-sm font-semibold text-gray-900">{template.label}</h4>
					<p className="text-xs text-tertiary line-clamp-2 max-h-0 group-hover:max-h-8 overflow-hidden transition-[max-height] duration-200">{template.description}</p>
				</div>
			</button>
		);
	}

	return (
		<button type="button" onClick={onClick} disabled={disabled} className={`group flex flex-col h-44 text-left rounded-xl border-2 overflow-hidden transition-all ${buttonClasses}`}>
			<div className="relative flex-1 min-h-0">
				<TemplatePreviewImage template={template} />
				<span className="absolute top-2 left-2 px-2 py-0.5 text-[10px] font-medium bg-white/90 text-gray-700 rounded-full shadow-sm capitalize">{template.display_type}</span>
			</div>
			<div className="px-4 py-3 shrink-0">
				<h4 className="text-sm font-semibold text-gray-900">{template.label}</h4>
				<p className="text-xs text-tertiary line-clamp-2 max-h-0 group-hover:max-h-8 overflow-hidden transition-[max-height] duration-200">{template.description}</p>
			</div>
		</button>
	);
}

export default function CreateWidgetModal({ open, onOpenChange, onSuccess, onSubmit }: CreateWidgetModalProps) {
	const [error, setError] = useState<string | null>(null);
	const { templates, listTemplates } = useWidgets();
	const [isLoadingTemplates, setIsLoadingTemplates] = useState(false);
	const { planDetails } = usePlan();
	const { selectedBrand } = useBrands();
	const openUpgradeModal = useOpenUpgradeModal();

	const {
		register,
		handleSubmit,
		reset,
		setValue,
		watch,
		formState: { errors, isSubmitting },
	} = useForm<WidgetFormValues>({
		resolver: zodResolver(widgetFormSchema),
		defaultValues: {
			title: "",
			template: "",
			template_version: 0,
		},
	});

	const selectedTemplate = watch("template");
	const selectedVersion = watch("template_version");
	const selectedKey = selectedTemplate && selectedVersion ? `${selectedTemplate}:${selectedVersion}` : null;
	const supportedWidgetLevel = planDetails?.limits?.supported_widget_level ?? 0;

	useEffect(() => {
		if (open && !templates) {
			setIsLoadingTemplates(true);
			listTemplates().finally(() => setIsLoadingTemplates(false));
		}
	}, [open, templates, listTemplates]);

	useEffect(() => {
		if (open && templates && templates.length > 0) {
			const first = templates[0];
			setValue("template", first.template);
			setValue("template_version", first.version);
		}
	}, [open, templates, setValue]);

	const handleTemplateSelect = (template: WidgetTemplateListItem) => {
		setValue("template", template.template, { shouldValidate: true });
		setValue("template_version", template.version, { shouldValidate: true });
	};

	const handleFormSubmit = async (data: WidgetFormValues) => {
		setError(null);
		try {
			const result = await onSubmit(data);
			if (result.success && result.data) {
				onSuccess(result.data.id);
				handleReset();
				onOpenChange(false);
			} else {
				setError("Failed to create widget. Please try again.");
			}
		} catch (err) {
			setError(err instanceof Error ? err.message : "An error occurred while creating the widget.");
		}
	};

	const handleReset = () => {
		reset();
		setError(null);
	};

	const handleOpenChange = (newOpen: boolean) => {
		if (!newOpen) {
			handleReset();
		}
		onOpenChange(newOpen);
	};

	const handleUpgradeClick = () => {
		onOpenChange(false);
		openUpgradeModal({
			context: "premium_widget",
			userRole: selectedBrand?.role,
			onMaybeLater: () => {
				onOpenChange(true);
			},
		});
	};

	return (
		<Dialog.Root open={open} onOpenChange={handleOpenChange}>
			<Dialog.Portal>
				<Dialog.Overlay className="fixed inset-0 bg-black/50 z-50 modal-overlay" />
				<Dialog.Content className="fixed left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 bg-white rounded-xl shadow-xl z-50 w-full max-w-4xl modal-content overflow-hidden">
					<form onSubmit={handleSubmit(handleFormSubmit)} className="flex flex-col max-h-[80vh]">
						<div className="px-6 pt-6 pb-4 border-b border-gray-200">
							<Dialog.Title className="text-base font-semibold text-gray-900 leading-base">Create Widget</Dialog.Title>
							<Dialog.Description className="text-sm text-gray-500 leading-sm mt-1">
								Choose a template and enter a title. You&apos;ll be taken to the builder to customize it.
							</Dialog.Description>
						</div>

						<div className="overflow-y-auto px-6 py-4">
							<div className="space-y-5">
								<div className="max-w-sm">
									<Input
										id="widget-title"
										type="text"
										label="Widget Title"
										placeholder="Enter widget title"
										required
										disabled={isSubmitting}
										error={errors.title?.message}
										{...register("title")}
									/>
								</div>

								<div>
									<label className="block text-sm font-medium text-gray-700 mb-2">
										Template <span className="text-red-500">*</span>
									</label>
									{isLoadingTemplates ? (
										<div className="grid grid-cols-3 gap-3">
											{Array.from({ length: 3 }).map((_, i) => (
												<div key={i} className="rounded-xl border-2 border-gray-200 p-3 animate-pulse">
													<div className="h-20 w-full rounded-lg bg-gray-100 mb-2" />
													<div className="h-3 w-16 bg-gray-100 rounded mb-1" />
													<div className="h-2.5 w-24 bg-gray-100 rounded" />
												</div>
											))}
										</div>
									) : (
										<div className="grid grid-cols-3 gap-3">
											{(templates || []).map((template) => {
												const key = `${template.template}:${template.version}`;
												const isSelected = selectedKey === key;
												return (
													<TemplateItem
														key={key}
														onClick={() => handleTemplateSelect(template)}
														disabled={isSubmitting}
														template={template}
														supportedWidgetLevel={supportedWidgetLevel}
														isSelected={isSelected}
														onUpgradeClick={handleUpgradeClick}
													/>
												);
											})}
										</div>
									)}
									{errors.template && <p className="mt-1.5 text-sm text-red-600 leading-sm">{errors.template.message}</p>}
								</div>
							</div>
						</div>

						<div className="px-6 py-4 border-t border-gray-200 flex justify-between items-center">
							<Dialog.Close asChild>
								<Button type="button" variant="soft">
									Cancel
								</Button>
							</Dialog.Close>
							<div className="flex items-center gap-3">
								{error && <p className="text-sm text-red-600 leading-sm">{error}</p>}
								<Button type="submit" variant="brand" disabled={isSubmitting}>
									{isSubmitting ? "Creating..." : "Create"}
								</Button>
							</div>
						</div>
					</form>

					<Dialog.Close asChild>
						<button className="absolute top-7 right-6 text-gray-400 hover:text-gray-600 transition-colors" aria-label="Close">
							<svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
								<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
							</svg>
						</button>
					</Dialog.Close>
				</Dialog.Content>
			</Dialog.Portal>
		</Dialog.Root>
	);
}
